import { Avatar, AvatarFallback } from "@car/ui/components/avatar";
import { Button } from "@car/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@car/ui/components/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@car/ui/components/popover";
import { PushSwitch, dropPushSubscription } from "@car/ui/components/push-switch";
import { Separator } from "@car/ui/components/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@car/ui/components/sidebar";
import {
  CalendarAdd01Icon,
  Cancel01Icon,
  Car03Icon,
  CarAlertIcon,
  ContractsIcon,
  CreditCardIcon,
  DashboardSquare01Icon,
  DeliveryTruck01Icon,
  Invoice01Icon,
  Logout01Icon,
  Medal01Icon,
  Message01Icon,
  Moon02Icon,
  MoneyReceive02Icon,
  Notification02Icon,
  SquareArrowLeft01Icon,
  Sun03Icon,
  TowTruckIcon,
  UnfoldMoreIcon,
  UserCircleIcon,
  Wrench01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import type { IconSvgElement } from "@hugeicons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  Outlet,
  createFileRoute,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useTheme } from "next-themes";

import { useState } from "react";

import { PanelQueryError } from "@/components/panel-bits";
import { authClient, isCustomerUser } from "@/lib/auth-client";
import { telHref, useSiteContent } from "@/lib/content";
import { faNumber, toFaDigits } from "@/lib/format";
import type { PanelNotification } from "@/lib/panel";
import { client, orpc } from "@/utils/orpc";

import { cn, SEP } from "@car/ui/lib/utils";

export const Route = createFileRoute("/panel")({
  ssr: false,
  beforeLoad: async ({ location }) => {
    const session = await authClient.getSession().catch(() => null);
    if (!session?.data) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    if (!isCustomerUser(session.data.user)) {
      // حساب کارمند به پنل مشتریان راه ندارد؛ نشست مشترک با پنل مدیریت را نمی‌بندیم و فقط به صفحه ورود می‌فرستیم
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    return { customerPhone: session.data.user.phoneNumber ?? "" };
  },
  component: PanelLayout,
});

type BadgeKey = "orders" | "services" | "contracts" | "payLinks";
type NavItem = {
  to: string;
  label: string;
  icon: IconSvgElement;
  exact?: boolean;
  badge?: BadgeKey;
};

const navGroups: { label: string | null; items: NavItem[] }[] = [
  {
    label: null,
    items: [{ to: "/panel", label: "داشبورد", icon: DashboardSquare01Icon, exact: true }],
  },
  {
    label: "خودرو",
    items: [
      { to: "/panel/vehicles", label: "خودروهای من", icon: Car03Icon },
      { to: "/panel/orders", label: "سفارش‌های در راه", icon: DeliveryTruck01Icon, badge: "orders" },
      { to: "/panel/contracts", label: "قراردادها", icon: ContractsIcon, badge: "contracts" },
    ],
  },
  {
    label: "خدمات",
    items: [
      { to: "/panel/appointments", label: "رزرو خدمات", icon: Wrench01Icon, badge: "services" },
      { to: "/panel/requests", label: "درخواست‌ها", icon: TowTruckIcon },
    ],
  },
  {
    label: "مالی",
    items: [
      {
        to: "/panel/transactions",
        label: "تراکنش‌ها",
        icon: MoneyReceive02Icon,
        badge: "payLinks",
      },
      { to: "/panel/history", label: "سوابق و فاکتورها", icon: Invoice01Icon },
      { to: "/panel/installments", label: "اقساط و تسهیلات", icon: CreditCardIcon },
    ],
  },
  {
    label: "باشگاه و پشتیبانی",
    items: [
      { to: "/panel/club", label: "باشگاه مشتریان", icon: Medal01Icon },
      { to: "/panel/tickets", label: "پشتیبانی و تیکت‌ها", icon: Message01Icon },
    ],
  },
];

const profileItem: NavItem = { to: "/panel/profile", label: "پروفایل", icon: UserCircleIcon };
const routeItems = [...navGroups.flatMap((group) => group.items), profileItem];

const findActiveItem = (pathname: string) =>
  routeItems.find((item) => (item.exact ? pathname === item.to : pathname.startsWith(item.to)));

// سایدبار کوکی sidebar_state را می‌نویسد؛ پنل فقط سمت کاربر رندر می‌شود پس همین‌جا خوانده می‌شود.
const sidebarDefaultOpen = () => !document.cookie.includes("sidebar_state=false");

// آیکن نوار کناری سمت راست است؛ راهنمای حالت جمع‌شده باید به سمت محتوا باز شود.
const tip = (children: string) => ({ children, side: "left" as const });

function PanelLayout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const activeItem = findActiveItem(pathname);

  return (
    <SidebarProvider defaultOpen={sidebarDefaultOpen()}>
      <PanelSidebar pathname={pathname} />

      <SidebarInset>
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b bg-background/95 px-4 backdrop-blur-lg">
          {/* روی گوشی تنها راه ناوبری پنل است؛ هدف لمسی بزرگ (۴۰ و روی لمسی ۴۴ پیکسل) */}
          <SidebarTrigger size="icon" aria-label="باز و بسته کردن منو" />
          <Separator orientation="vertical" className="h-5" />
          <span className="text-sm font-bold">{activeItem?.label ?? "پنل مشتریان"}</span>
          <div className="ms-auto flex items-center gap-1">
            <NotificationBell />
          </div>
          <Button variant="ghost" size="sm" nativeButton={false} render={<Link to="/" />}>
            <HugeiconsIcon icon={SquareArrowLeft01Icon} strokeWidth={1.8} />
            <span className="hidden sm:inline">بازگشت به سایت</span>
          </Button>
        </header>
        <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

function PanelSidebar({ pathname }: { pathname: string }) {
  const { contact } = useSiteContent();
  const { isMobile, setOpenMobile, state } = useSidebar();
  const overviewQuery = useQuery(orpc.panel.overview.queryOptions());
  const activeItem = findActiveItem(pathname);

  const overview = overviewQuery.data;
  const counts = {
    orders: overview?.orders.length ?? 0,
    services: (overview?.appointments.length ?? 0) + (overview?.repairOrders.length ?? 0),
    contracts: overview?.pendingContracts ?? 0,
    payLinks: overview?.pendingPayLinks ?? 0,
  } satisfies Record<BadgeKey, number>;
  // نوبت سرویس برای یک خودرو گرفته می‌شود: تا خودرویی ثبت نشده دکمه رنگ اصلی نمی‌گیرد
  const noVehicle = overview !== undefined && overview.garage.length === 0;
  // پیوندها را خود سایدبار می‌بندد؛ منوی حساب با navigate می‌رود و این را لازم دارد
  const closeOnMobile = () => {
    if (isMobile) setOpenMobile(false);
  };

  return (
    <Sidebar side="right" collapsible="icon">
      <SidebarHeader className="gap-3 p-3 group-data-[collapsible=icon]:p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              render={<Link to="/" aria-label="تابان خودرو، صفحه اصلی" />}
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-brand-ice p-1 ring-1 ring-sidebar-border">
                <img src="/logo.webp" alt="" width={320} height={227} className="w-full" />
              </span>
              <span className="grid leading-tight">
                <span className="font-black">تابان خودرو</span>
                <span className="text-xs text-muted-foreground">پنل مشتریان</span>
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem className="mt-1">
            <SidebarMenuButton
              tooltip={tip("رزرو نوبت سرویس")}
              render={<Link to="/panel/appointments/new" />}
              className={cn(
                "h-10 justify-center gap-2 font-bold transition-[width,height,padding,background-color] group-data-[collapsible=icon]:justify-start group-data-[collapsible=icon]:p-[7px]! [&_svg]:size-[18px]",
                noVehicle
                  ? "border bg-transparent text-sidebar-foreground"
                  : "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 hover:text-primary-foreground active:bg-primary/85 active:text-primary-foreground",
              )}
            >
              <HugeiconsIcon icon={CalendarAdd01Icon} strokeWidth={2} />
              <span>رزرو نوبت سرویس</span>
            </SidebarMenuButton>
            {noVehicle ? (
              <p className="mt-1 px-1 text-xs text-muted-foreground group-data-[collapsible=icon]:hidden">
                بعد از ثبت خودرو در پنل فعال می‌شود.
              </p>
            ) : null}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="gap-0 py-1">
        {navGroups.map((group) => (
          <SidebarGroup
            key={group.label ?? "main"}
            className="px-3 py-1.5 group-data-[collapsible=icon]:px-2"
          >
            {group.label ? (
              <SidebarGroupLabel className="h-7 text-xs font-bold text-muted-foreground">
                {group.label}
              </SidebarGroupLabel>
            ) : null}
            <SidebarGroupContent>
              <SidebarMenu className="gap-0.5">
                {group.items.map((item) => {
                  const isActive = activeItem?.to === item.to;
                  const count = item.badge ? counts[item.badge] : 0;
                  return (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton
                        tooltip={tip(count > 0 ? `${item.label} (${faNumber(count)})` : item.label)}
                        isActive={isActive}
                        render={<Link to={item.to} aria-current={isActive ? "page" : undefined} />}
                        className={`h-9 gap-3 px-2.5 text-sidebar-foreground/80 pointer-coarse:h-11 group-data-[collapsible=icon]:p-[7px]! data-active:bg-primary/10 data-active:font-bold data-active:text-primary hover:data-active:bg-primary/15 hover:data-active:text-primary [&_svg]:size-[18px] ${count > 0 ? "pe-10" : ""}`}
                      >
                        <HugeiconsIcon icon={item.icon} strokeWidth={isActive ? 2.1 : 1.7} />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                      {count > 0 ? (
                        <>
                          <SidebarMenuBadge className="top-2! min-w-6 rounded-full bg-primary/10 px-1.5 font-bold text-primary peer-hover/menu-button:text-primary peer-data-active/menu-button:bg-primary peer-data-active/menu-button:text-primary-foreground">
                            {faNumber(count)}
                          </SidebarMenuBadge>
                          <span
                            aria-hidden
                            className="pointer-events-none absolute top-1 end-1 hidden size-2 rounded-full bg-primary ring-2 ring-sidebar group-data-[collapsible=icon]:block"
                          />
                        </>
                      ) : null}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="gap-2 border-t border-sidebar-border p-3 group-data-[collapsible=icon]:p-2">
        <SidebarMenu className="gap-1">
          {/* بدون شماره امداد در مدیریت محتوا، این راهنما کلاً نمایش داده نمی‌شود */}
          {contact.hotlinePhone ? (
            <SidebarMenuItem>
              <SidebarMenuButton
                tooltip={tip("امداد جاده‌ای ۲۴ ساعته")}
                render={<a href={telHref(contact.hotlinePhone)} />}
                className="h-10 gap-3 px-2.5 group-data-[collapsible=icon]:p-[7px]! [&_svg]:size-[18px]"
              >
                <HugeiconsIcon icon={CarAlertIcon} strokeWidth={1.8} className="text-destructive" />
                <span className="grid leading-tight">
                  <span className="text-[13px] font-bold">امداد جاده‌ای ۲۴ ساعته</span>
                  <span dir="ltr" className="text-start text-xs text-muted-foreground">
                    {toFaDigits(contact.hotlinePhone)}
                  </span>
                </span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ) : null}
          <SidebarMenuItem>
            <AccountMenu
              profile={overview?.profile ?? null}
              club={overview?.club ?? null}
              collapsed={state === "collapsed" && !isMobile}
              onNavigate={closeOnMobile}
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

type ClubSummary = { tier: { label: string }; balance: number };
type ProfileName = { firstName: string; lastName: string };

function AccountMenu({
  profile,
  club,
  collapsed,
  onNavigate,
}: {
  profile: ProfileName | null;
  club: ClubSummary | null;
  collapsed: boolean;
  onNavigate: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { resolvedTheme, setTheme } = useTheme();
  const { customerPhone } = Route.useRouteContext();

  const fullName =
    profile && (profile.firstName || profile.lastName)
      ? `${profile.firstName} ${profile.lastName}`.trim()
      : "حساب کاربری";
  const phone = toFaDigits(customerPhone);
  const isDark = resolvedTheme === "dark";

  const go = (to: string) => {
    onNavigate();
    navigate({ to });
  };
  const logout = async () => {
    await dropPushSubscription().catch(() => null);
    await authClient.signOut().catch(() => null);
    // داده‌های این مشتری نباید برای ورود بعدی روی همین مرورگر بماند
    queryClient.clear();
    navigate({ to: "/" });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <SidebarMenuButton
            size="lg"
            className="gap-3 px-2 data-popup-open:bg-sidebar-accent"
            aria-label={`منوی حساب ${fullName}`}
          />
        }
      >
        <Avatar className="size-8 shrink-0 rounded-lg">
          <AvatarFallback className="rounded-lg bg-primary/10 font-black text-primary">
            {profile?.firstName.slice(0, 1) || "؟"}
          </AvatarFallback>
        </Avatar>
        <span className="grid min-w-0 flex-1 leading-tight">
          <span className="truncate font-bold">{fullName}</span>
          <span className="truncate text-xs text-muted-foreground">
            {club ? `سطح ${club.tier.label}${SEP}${faNumber(club.balance)} امتیاز` : phone}
          </span>
        </span>
        <HugeiconsIcon
          icon={UnfoldMoreIcon}
          strokeWidth={1.8}
          className="ms-auto text-muted-foreground"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={collapsed ? "left" : "top"}
        align={collapsed ? "end" : "center"}
        sideOffset={8}
        className="w-60"
        lang="fa"
        dir="rtl"
      >
        <DropdownMenuGroup>
          <DropdownMenuLabel className="grid gap-0.5 py-2">
            <span className="text-sm font-bold text-popover-foreground">{fullName}</span>
            <span dir="ltr" className="text-end">
              {phone}
            </span>
          </DropdownMenuLabel>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => go("/panel/profile")}>
            <HugeiconsIcon icon={UserCircleIcon} strokeWidth={1.8} />
            پروفایل و اطلاعات تماس
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => go("/panel/club")}>
            <HugeiconsIcon icon={Medal01Icon} strokeWidth={1.8} />
            امتیازها و جوایز
          </DropdownMenuItem>
          <DropdownMenuItem
            closeOnClick={false}
            onClick={() => setTheme(isDark ? "light" : "dark")}
          >
            <HugeiconsIcon icon={isDark ? Sun03Icon : Moon02Icon} strokeWidth={1.8} />
            {isDark ? "پوسته روشن" : "پوسته تیره"}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => go("/")}>
            <HugeiconsIcon icon={SquareArrowLeft01Icon} strokeWidth={1.8} />
            بازگشت به سایت
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onClick={logout}>
          <HugeiconsIcon icon={Logout01Icon} strokeWidth={1.8} />
          خروج از حساب
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotificationItem({
  notification,
  onNavigate,
  onDismiss,
  dismissing,
}: {
  notification: PanelNotification;
  onNavigate: () => void;
  onDismiss: () => void;
  dismissing: boolean;
}) {
  return (
    <div className="flex items-start gap-1">
      <NotificationLink notification={notification} onNavigate={onNavigate} />
      {notification.dismissible ? (
        <Button
          variant="ghost"
          size="icon-sm"
          className="mt-1.5 shrink-0 text-muted-foreground"
          aria-label={`بستن اعلان: ${notification.title}`}
          disabled={dismissing}
          onClick={onDismiss}
        >
          <HugeiconsIcon icon={Cancel01Icon} strokeWidth={1.8} />
        </Button>
      ) : null}
    </div>
  );
}

function NotificationLink({
  notification,
  onNavigate,
}: {
  notification: PanelNotification;
  onNavigate: () => void;
}) {
  const body = (
    <>
      <span
        className={
          notification.severity === "warning"
            ? "mt-1.5 size-2 shrink-0 rounded-full bg-destructive"
            : "mt-1.5 size-2 shrink-0 rounded-full bg-primary/60"
        }
        aria-hidden
      />
      <span className="grid gap-0.5">
        <span className="text-sm font-bold">{notification.title}</span>
        <span className="text-xs leading-5 text-muted-foreground">{notification.description}</span>
      </span>
    </>
  );
  const className =
    "flex min-w-0 flex-1 items-start gap-2.5 rounded-lg p-2.5 text-start transition-colors hover:bg-muted";

  if (notification.ownedVehicleId !== null) {
    return (
      <Link
        to="/panel/vehicles/$ownedId"
        params={{ ownedId: notification.ownedVehicleId }}
        className={className}
        onClick={onNavigate}
      >
        {body}
      </Link>
    );
  }
  return (
    <Link to={notification.to} className={className} onClick={onNavigate}>
      {body}
    </Link>
  );
}

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  // همان پرس‌وجوی overview سایدبار و داشبورد: یک درخواست برای هر سه، نه یکی جدا برای زنگوله
  const query = useQuery(
    orpc.panel.overview.queryOptions({ select: (data) => data.notifications }),
  );
  const pushConfig = useQuery(
    orpc.push.config.queryOptions({ enabled: open, staleTime: Infinity }),
  );
  const notifications = query.data ?? [];
  const dismissible = notifications.filter((item) => item.dismissible);
  const dismiss = useMutation(
    orpc.panel.notifications.dismiss.mutationOptions({
      onSuccess: () => queryClient.invalidateQueries({ queryKey: orpc.panel.overview.key() }),
    }),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            aria-label={
              notifications.length > 0
                ? `${faNumber(notifications.length)} اعلان خوانده‌نشده`
                : "اعلان‌ها"
            }
          />
        }
      >
        <HugeiconsIcon icon={Notification02Icon} strokeWidth={1.8} />
        {notifications.length > 0 ? (
          <span className="absolute -top-0.5 -end-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-destructive px-1 text-xs font-black text-white">
            {notifications.length > 9 ? "۹+" : faNumber(notifications.length)}
          </span>
        ) : null}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 max-w-[92vw] p-2" lang="fa" dir="rtl">
        <div className="flex items-center justify-between gap-2 px-2 py-1.5">
          <span className="font-bold">
            اعلان‌ها
            {notifications.length > 0 ? (
              <span className="ms-1.5 text-xs font-normal text-muted-foreground">
                {faNumber(notifications.length)} مورد
              </span>
            ) : null}
          </span>
          {dismissible.length > 1 ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={dismiss.isPending}
              onClick={() => dismiss.mutate({ ids: dismissible.map((item) => item.id) })}
            >
              بستن همه
            </Button>
          ) : null}
        </div>
        <Separator className="mb-1" />
        {query.isPending ? (
          <p className="p-3 text-sm text-muted-foreground">در حال بارگذاری…</p>
        ) : query.isError ? (
          <PanelQueryError title="اعلان‌ها بارگیری نشد" query={query} />
        ) : notifications.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">
            فعلاً همه‌چیز مرتب است؛ اعلان جدیدی ندارید.
          </p>
        ) : (
          <div className="grid max-h-96 gap-0.5 overflow-y-auto">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onNavigate={() => setOpen(false)}
                onDismiss={() => dismiss.mutate({ ids: [notification.id] })}
                dismissing={dismiss.isPending}
              />
            ))}
          </div>
        )}
        {pushConfig.data?.vapidKey ? (
          <>
            <Separator className="my-1" />
            <PushSwitch
              vapidKey={pushConfig.data.vapidKey}
              onSubscribe={(keys) => client.push.subscribe(keys)}
              onUnsubscribe={(endpoint) => client.push.unsubscribe({ endpoint })}
            />
          </>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
